import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { PageProvider } from './context/PageContext';
import { Toaster } from './components/ui/sonner';
import AppShell from './components/layout/AppShell';
import AuthPage from './pages/AuthPage';

// Blocks the Developer section for memberships without developer access
// (prospect-facing demo logins). Nav hiding alone is cosmetic; this stops
// direct URLs too.
const RequireDeveloperAccess = ({ children }) => {
  const { developerAccess } = useAuth();
  return developerAccess ? children : <Navigate to="/" replace />;
};

// Internal Developer tools (diagnostics, onboarding invites, transcripts):
// TutorTracer staff only, whatever the customer's own access settings.
const RequireStaff = ({ children }) => {
  const { isStaff } = useAuth();
  if (isStaff === null) return null; // staff check still loading
  return isStaff ? children : <Navigate to="/" replace />;
};
import Dashboard from './pages/Dashboard';
import Issues from './pages/Issues';
import Punctuality from './pages/Punctuality';
import Lessons from './pages/Lessons';
import LessonDetail from './pages/LessonDetail';
import Tutors from './pages/Tutors';
import EmailNotifications from './pages/EmailNotifications';
import Developer from './pages/Developer';
import Settings from './pages/Settings';
import IssueGuardrails from './pages/IssueGuardrails';
import Support from './pages/Support';
import Assistant from './pages/Assistant';
import Policies from './pages/Policies';
import PolicyDetail from './pages/PolicyDetail';
import TutorDetail from './pages/TutorDetail';
import Students from './pages/Students';
import StudentDetail from './pages/StudentDetail';
import Library from './pages/Library';
import Billing from './pages/Billing';
import DeveloperStatistics from './pages/DeveloperStatistics';
import DeveloperApi from './pages/DeveloperApi';
import ApiWorkspace from './pages/ApiWorkspace';
import DeveloperApiDocs from './pages/DeveloperApiDocs';
import DeveloperSpeakerId from './pages/DeveloperSpeakerId';
import DeveloperGeminiLogs from './pages/DeveloperGeminiLogs';
import DeveloperTranscripts from './pages/DeveloperTranscripts';
import DeveloperProcessed from './pages/DeveloperProcessed';
import Onboarding from './pages/Onboarding';
import Terms from './pages/Terms';
import OnboardIntake from './pages/OnboardIntake';
import Feedback from './pages/Feedback';
import IntegrationWizard from './pages/IntegrationWizard';
import Export from './pages/Export';
import './App.css';
import ResetPassword from './pages/ResetPassword';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#09090b]' : 'bg-[#fafafa]'}`}>
        <div className={`animate-pulse ${isDark ? 'text-[#a1a1aa]' : 'text-[#666666]'}`}>Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return children;
};

const AppRoutes = () => {
  const { user, loading } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#09090b]' : 'bg-[#fafafa]'}`}>
        <div className={`w-8 h-8 border-2 border-t-current rounded-full animate-spin ${isDark ? 'border-[#27272a] text-white' : 'border-[#e5e5e5] text-[#1a1a1a]'}`} />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/auth" element={user ? <Navigate to="/" replace /> : <AuthPage />} />
      {/* Public — terms must be reachable without an account */}
      <Route path="/terms" element={<Terms />} />
      {/* Public — prospect onboarding by tokenised link */}
      <Route path="/onboard/:token" element={<OnboardIntake />} />
      {/* Public — landing page for the forgot-password email link */}
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/onboarding" element={<Onboarding />} />

      <Route path="/*" element={
        <ProtectedRoute>
          <AppShell>
            <Routes>
              <Route index element={<Dashboard />} />
              <Route path="issues" element={<Issues />} />
              <Route path="punctuality" element={<Punctuality />} />
              <Route path="lessons" element={<Lessons />} />
              <Route path="lessons/:sessionId" element={<LessonDetail />} />
              <Route path="star-performers" element={<Navigate to="/tutors" replace />} />
              <Route path="policies" element={<Policies />} />
              <Route path="policies/:documentId" element={<PolicyDetail />} />
              <Route path="assistant" element={<Assistant />} />
              <Route path="intelligence" element={<Navigate to="/assistant" replace />} />
              <Route path="tutors" element={<Tutors />} />
              <Route path="tutors/:tutorName" element={<TutorDetail />} />
              <Route path="students" element={<Students />} />
              <Route path="students/:studentName" element={<StudentDetail />} />
              <Route path="library" element={<Library />} />
              <Route path="billing" element={<Billing />} />
              <Route path="email-notifications" element={<RequireDeveloperAccess><EmailNotifications /></RequireDeveloperAccess>} />
              <Route path="api-workspace" element={<ApiWorkspace />} />
              <Route path="developer" element={<RequireStaff><Developer /></RequireStaff>} />
              <Route path="developer/api" element={<RequireStaff><DeveloperApi /></RequireStaff>} />
              <Route path="developer/api-docs" element={<RequireStaff><DeveloperApiDocs /></RequireStaff>} />
              <Route path="developer/statistics" element={<RequireStaff><DeveloperStatistics /></RequireStaff>} />
              <Route path="developer/speaker-id" element={<RequireStaff><DeveloperSpeakerId /></RequireStaff>} />
              <Route path="developer/gemini-logs" element={<RequireStaff><DeveloperGeminiLogs /></RequireStaff>} />
              <Route path="developer/transcripts" element={<RequireStaff><DeveloperTranscripts /></RequireStaff>} />
              <Route path="developer/processed" element={<RequireStaff><DeveloperProcessed /></RequireStaff>} />
              <Route path="settings" element={<Settings />} />
              <Route path="settings/issue-guardrails" element={<IssueGuardrails />} />
              <Route path="support" element={<Support />} />
              <Route path="feedback" element={<Feedback />} />
              <Route path="integration-wizard" element={<IntegrationWizard />} />
              <Route path="export" element={<Export />} />
            </Routes>
          </AppShell>
        </ProtectedRoute>
      } />
    </Routes>
  );
};

function App() {
  // onboard.tutortracer.com serves ONLY the prospect intake page — no login
  // screen, no app shell. Every path there is treated as /<token>.
  if (window.location.hostname.startsWith('onboard.')) {
    return <OnboardIntake />;
  }
  return (
    <ThemeProvider>
      <AuthProvider>
        <PageProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="bottom-right" />
          </BrowserRouter>
        </PageProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
